/* eslint-disable react/react-in-jsx-scope */
import {
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
} from '@backstage/core-components';
import { MTableToolbar } from '@material-table/core';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMemo, useState } from 'react';
import type { JiraIssue } from '../api/JiraApi';
import {
  JIRA_FILTER_NO_LABELS,
  JIRA_FILTER_UNASSIGNED,
  distinctAssigneeKeys,
  distinctLabelKeys,
  distinctStatuses,
  distinctTypes,
  filterJiraIssues,
} from './filterJiraIssues';
import {
  jiraIssuesToTableRows,
  type JiraIssueTableRow,
} from './jiraIssuesToTableRows';

const useStyles = makeStyles(theme => ({
  toolbarTitleRow: {
    padding: theme.spacing(2, 2.5, 1, 2.5),
  },
  toolbarFilterSearchRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 2.5, 1.5, 2.5),
  },
  toolbarFiltersGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 240px',
    minWidth: 0,
  },
  toolbarSearchGroup: {
    flex: '0 0 auto',
    minWidth: 200,
    '& .MuiToolbar-root': {
      minHeight: 48,
      paddingLeft: 0,
      paddingRight: theme.spacing(1),
    },
  },
  filterControl: {
    minWidth: 160,
    maxWidth: 220,
  },
  typeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  assigneeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  smallAvatar: {
    width: 20,
    height: 20,
    fontSize: '0.65rem',
  },
  labelsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxWidth: 220,
  },
  labelChip: {
    height: 20,
    fontSize: '0.65rem',
  },
}));

function statusColor(category: string): 'default' | 'primary' | 'secondary' {
  if (category === 'done') return 'primary';
  if (category === 'indeterminate') return 'secondary';
  return 'default';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

interface Props {
  issues: JiraIssue[];
  loading: boolean;
  error?: Error;
  onSelectionChange: (rows: JiraIssue[]) => void;
  /** Increment or change to remount the table and reset internal selection (e.g. after clear). */
  tableRemountKey?: number;
  selectedCount: number;
  onClearSelections: () => void;
}

export function JiraIssuesTable({
  issues,
  loading,
  error,
  onSelectionChange,
  tableRemountKey = 0,
  selectedCount,
  onClearSelections,
}: Props) {
  const classes = useStyles();
  const theme = useTheme();

  const [showDoneClosed, setShowDoneClosed] = useState(false);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterAssignees, setFilterAssignees] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterLabels, setFilterLabels] = useState<string[]>([]);
  /** Bumps with filter changes so the table remounts and drops stale selection. */
  const [filterEpoch, setFilterEpoch] = useState(0);

  const bumpFiltersAndClearSelection = () => {
    setFilterEpoch(e => e + 1);
    onSelectionChange([]);
  };

  const typeOptions = useMemo(() => distinctTypes(issues), [issues]);
  const statusOptions = useMemo(() => distinctStatuses(issues), [issues]);
  const assigneeOptions = useMemo(() => distinctAssigneeKeys(issues), [issues]);
  const labelOptions = useMemo(() => distinctLabelKeys(issues), [issues]);

  const filteredIssues = useMemo(
    () =>
      filterJiraIssues(issues, {
        showDoneClosed,
        types: filterTypes,
        assignees: filterAssignees,
        statuses: filterStatuses,
        labels: filterLabels,
      }),
    [
      issues,
      showDoneClosed,
      filterTypes,
      filterAssignees,
      filterStatuses,
      filterLabels,
    ],
  );

  const rows: JiraIssueTableRow[] = useMemo(
    () => jiraIssuesToTableRows(filteredIssues),
    [filteredIssues],
  );

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const columns = [
    {
      title: 'Type',
      field: 'type',
      width: '120px',
      render: (row: JiraIssue) => (
        <Box className={classes.typeCell}>
          {row.typeIconUrl && (
            <img src={row.typeIconUrl} alt={row.type} width={16} height={16} />
          )}
          <Typography variant="caption">{row.type}</Typography>
        </Box>
      ),
    },
    {
      title: 'Key',
      field: 'key',
      width: '100px',
      render: (row: JiraIssue) => (
        <Link to={row.url} target="_blank" rel="noopener">
          {row.key}
        </Link>
      ),
    },
    {
      title: 'Summary',
      field: 'summary',
    },
    {
      title: 'Labels',
      field: 'labels',
      width: '200px',
      render: (row: JiraIssue) => {
        const labels = row.labels?.filter(Boolean) ?? [];
        if (labels.length === 0) {
          return (
            <Typography variant="caption" color="textSecondary">
              —
            </Typography>
          );
        }
        return (
          <Box className={classes.labelsCell}>
            {labels.map(label => (
              <Chip
                key={label}
                className={classes.labelChip}
                label={label}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        );
      },
    },
    {
      title: 'Assignee',
      field: 'assignee',
      width: '150px',
      render: (row: JiraIssue) =>
        row.assignee ? (
          <Box className={classes.assigneeCell}>
            <Tooltip title={row.assignee}>
              <Avatar
                className={classes.smallAvatar}
                src={row.assigneeAvatarUrl}
                alt={row.assignee}
              >
                {row.assignee.charAt(0)}
              </Avatar>
            </Tooltip>
            <Typography variant="caption" noWrap>
              {row.assignee}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="textSecondary">
            Unassigned
          </Typography>
        ),
    },
    {
      title: 'Status',
      field: 'status',
      width: '130px',
      render: (row: JiraIssue) => (
        <Chip
          className={classes.statusChip}
          label={row.status}
          color={statusColor(row.statusCategory)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      title: 'Updated',
      field: 'updated',
      width: '100px',
      render: (row: JiraIssue) => (
        <Tooltip title={new Date(row.updated).toLocaleString()}>
          <Typography variant="caption">{relativeTime(row.updated)}</Typography>
        </Tooltip>
      ),
    },
  ];

  const multiLabel = (selected: string[], emptyLabel: string) => {
    if (selected.length === 0) return emptyLabel;
    if (selected.length > 2) return `${selected.length} selected`;
    return selected
      .map(s => {
        if (s === JIRA_FILTER_UNASSIGNED) return 'Unassigned';
        if (s === JIRA_FILTER_NO_LABELS) return 'No labels';
        return s;
      })
      .join(', ');
  };

  const filterControls = (
    <>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={showDoneClosed}
            onChange={(_, checked) => {
              setShowDoneClosed(checked);
              bumpFiltersAndClearSelection();
            }}
          />
        }
        label="Show Closed"
      />
      <FormControl
        variant="outlined"
        size="small"
        className={classes.filterControl}
      >
        <InputLabel id="jira-filter-type-label">Type</InputLabel>
        <Select
          labelId="jira-filter-type-label"
          label="Type"
          multiple
          value={filterTypes}
          onChange={e => {
            setFilterTypes(e.target.value as string[]);
            bumpFiltersAndClearSelection();
          }}
          renderValue={s => multiLabel(s as string[], 'All types')}
        >
          {typeOptions.map(t => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.filterControl}
      >
        <InputLabel id="jira-filter-assignee-label">Assignee</InputLabel>
        <Select
          labelId="jira-filter-assignee-label"
          label="Assignee"
          multiple
          value={filterAssignees}
          onChange={e => {
            setFilterAssignees(e.target.value as string[]);
            bumpFiltersAndClearSelection();
          }}
          renderValue={s => multiLabel(s as string[], 'All assignees')}
        >
          {assigneeOptions.map(a => (
            <MenuItem key={a} value={a}>
              {a === JIRA_FILTER_UNASSIGNED ? 'Unassigned' : a}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.filterControl}
      >
        <InputLabel id="jira-filter-status-label">Status</InputLabel>
        <Select
          labelId="jira-filter-status-label"
          label="Status"
          multiple
          value={filterStatuses}
          onChange={e => {
            setFilterStatuses(e.target.value as string[]);
            bumpFiltersAndClearSelection();
          }}
          renderValue={s => multiLabel(s as string[], 'All statuses')}
        >
          {statusOptions.map(s => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.filterControl}
      >
        <InputLabel id="jira-filter-labels-label">Labels</InputLabel>
        <Select
          labelId="jira-filter-labels-label"
          label="Labels"
          multiple
          value={filterLabels}
          onChange={e => {
            setFilterLabels(e.target.value as string[]);
            bumpFiltersAndClearSelection();
          }}
          renderValue={s => multiLabel(s as string[], 'All labels')}
        >
          {labelOptions.map(l => (
            <MenuItem key={l} value={l}>
              {l === JIRA_FILTER_NO_LABELS ? 'No labels' : l}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  return (
    <Box>
      <Table
        key={`${tableRemountKey}-${filterEpoch}`}
        title=""
        components={{
          Toolbar: props => (
            <Box>
              <Box className={classes.toolbarTitleRow}>
                <Typography variant="h5" component="h2">
                  Issues
                </Typography>
              </Box>
              <Box className={classes.toolbarFilterSearchRow}>
                <Box className={classes.toolbarFiltersGroup}>
                  {filterControls}
                </Box>
                <Box className={classes.toolbarSearchGroup}>
                  <MTableToolbar {...props} />
                </Box>
              </Box>
            </Box>
          ),
        }}
        options={{
          showTitle: false,
          selection: true,
          pageSize: 10,
          pageSizeOptions: [10, 25, 50],
          padding: 'dense',
          rowStyle: rowData =>
            rowData.tableData?.checked
              ? {
                  backgroundColor: theme.palette.action.selected,
                  boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.light}`,
                }
              : {},
        }}
        columns={columns}
        data={rows}
        onSelectionChange={tableRows =>
          onSelectionChange(tableRows as JiraIssue[])
        }
      />
      {selectedCount > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button size="small" color="primary" onClick={onClearSelections}>
            Clear Selections
          </Button>
        </Box>
      )}
    </Box>
  );
}
